import { NavLink } from "react-router-dom";
import { employeeResultsTabLocked } from "@/lib/employeeNavLock";
import {
  lazyIntentHandlers,
  warmupEmployeeMaps,
  warmupOrgAssessmentEntry,
  warmupOrgEmployeeHome,
} from "@/routes/preloadChunks";

function desktopLinkClass(active: boolean, locked: boolean) {
  return (
    "relative flex min-h-[48px] shrink-0 items-center px-3 sm:px-4 font-[var(--font-display)] text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors border-b-[3px] " +
    (locked
      ? "cursor-not-allowed border-transparent text-[var(--color-text-subtle)] opacity-50 pointer-events-none"
      : active
        ? "border-[var(--color-highlight-blue)] text-[var(--color-highlight-blue)]"
        : "border-transparent text-[var(--color-steel-gray)] hover:border-[var(--color-border-muted)] hover:text-[var(--color-white)]")
  );
}

/**
 * Primary sections for team members on `lg`+, where the bottom tabs are hidden. Program is
 * not a tab: it is reached from the home page cards.
 */
export function EmployeeDesktopNav({
  employeeId,
  fcComplete,
  fcCompleteSticky,
}: {
  employeeId: string | null;
  fcComplete: boolean;
  fcCompleteSticky: boolean;
}) {
  const resultsLocked = employeeResultsTabLocked(employeeId, fcComplete, fcCompleteSticky);
  const mapsHref = employeeId ? `/employee/${employeeId}/maps` : "/org";

  return (
    <nav className="flex flex-wrap items-stretch gap-x-1 gap-y-0" aria-label="Primary sections">
      <NavLink
        to="/org"
        end
        className={({ isActive }) => desktopLinkClass(isActive, false)}
        {...lazyIntentHandlers(warmupOrgEmployeeHome)}
      >
        Home
      </NavLink>
      <NavLink
        to="/org/assessment"
        className={({ isActive }) => desktopLinkClass(isActive, false)}
        {...lazyIntentHandlers(warmupOrgAssessmentEntry)}
      >
        Assess
      </NavLink>
      {resultsLocked ? (
        <span
          className={desktopLinkClass(false, true)}
          title="Complete your functional capacity assessment first."
        >
          Results
          <span className="sr-only">Locked until functional capacity is complete.</span>
        </span>
      ) : (
        <NavLink
          to={mapsHref}
          className={({ isActive }) => desktopLinkClass(isActive, false)}
          {...lazyIntentHandlers(warmupEmployeeMaps)}
        >
          Results
        </NavLink>
      )}
    </nav>
  );
}

function navClass(active: boolean, locked: boolean) {
  return (
    "flex min-h-[44px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 font-[var(--font-display)] text-[10px] font-semibold uppercase tracking-[0.06em] transition-colors sm:text-[11px] " +
    (locked
      ? "cursor-not-allowed text-[var(--color-text-subtle)] opacity-45"
      : active
        ? "text-[var(--color-highlight-blue)]"
        : "text-[var(--color-steel-gray)] hover:text-[var(--color-white)]")
  );
}

export function EmployeeBottomNav({
  employeeId,
  fcComplete,
  fcCompleteSticky,
}: {
  employeeId: string | null;
  fcComplete: boolean;
  fcCompleteSticky: boolean;
}) {
  const resultsLocked = employeeResultsTabLocked(employeeId, fcComplete, fcCompleteSticky);
  const mapsHref = employeeId ? `/employee/${employeeId}/maps` : "/org";

  return (
    <nav
      className="flex border-t border-[var(--color-border)] bg-[var(--color-charcoal)] pb-[max(0.35rem,env(safe-area-inset-bottom))] pt-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
      aria-label="Primary"
    >
      <NavLink
        to="/org"
        end
        className={({ isActive }) => navClass(isActive, false)}
        {...lazyIntentHandlers(warmupOrgEmployeeHome)}
      >
        Home
      </NavLink>
      <NavLink
        to="/org/assessment"
        className={({ isActive }) => navClass(isActive, false)}
        {...lazyIntentHandlers(warmupOrgAssessmentEntry)}
      >
        Assess
      </NavLink>
      {resultsLocked ? (
        <span
          className={navClass(false, true)}
          title="Complete your functional capacity assessment first."
        >
          Results
          <span className="sr-only">Locked until functional capacity is complete.</span>
        </span>
      ) : (
        <NavLink
          to={mapsHref}
          className={({ isActive }) => navClass(isActive, false)}
          {...lazyIntentHandlers(warmupEmployeeMaps)}
        >
          Results
        </NavLink>
      )}
    </nav>
  );
}
